import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import api from '../axios_Interceptor/api';
import { SERVER_ADDRESS } from '../server';

type Delivery = { product_seq: number; product_name: string; addr_recipient: string; addr_phone: string; addr_num: string; addr_basic: string; addr_detail: string };
export default function ProductDeliverySection({ orderSeq, refreshKey }: { orderSeq: number; refreshKey?: number }) {
  const [products, setProducts] = useState<Delivery[]>([]);
  const [canEdit, setCanEdit] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<Delivery | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState('');
  const version = useRef(0), inFlight = useRef(false);
  const load = useCallback(async () => {
    const request = ++version.current;
    setLoading(true); setError(''); setProducts([]); setDraft(null); setSaved('');
    try {
      const { data } = await api.get(`${SERVER_ADDRESS}orders/product-deliveries`, { params: { order_seq: orderSeq }, timeout: 10000 });
      if (request !== version.current) return;
      if (!Array.isArray(data?.products)) throw new Error('Invalid delivery response');
      setProducts(data.products); setCanEdit(data.can_edit_address === true); setReason(data.address_edit_locked_reason || '');
    } catch { if (request === version.current) setError('개별 배송지를 불러오지 못했습니다. 다시 시도해주세요.'); }
    finally { if (request === version.current) setLoading(false); }
  }, [orderSeq]);
  useEffect(() => { void load(); return () => { version.current += 1; }; }, [load, refreshKey]);
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft || inFlight.current) return;
    const request = version.current;
    inFlight.current = true; setSaving(true); setError(''); setSaved('');
    try {
      const { data } = await api.patch(`${SERVER_ADDRESS}orders/product-delivery`, { order_seq: orderSeq, product_seq: draft.product_seq, addr_recipient: draft.addr_recipient, addr_phone: draft.addr_phone, addr_num: draft.addr_num, addr_basic: draft.addr_basic, addr_detail: draft.addr_detail }, { timeout: 10000 });
      if (request !== version.current) return;
      setProducts(rows => rows.map(row => row.product_seq === draft.product_seq ? { ...row, ...data } : row));
      setSaved(`${draft.product_name} 배송지를 저장했습니다.`); setDraft(null);
    } catch (failure: any) { if (request === version.current) setError(failure?.response?.data?.message || '배송지를 저장하지 못했습니다. 입력 내용을 확인하고 다시 시도해주세요.'); }
    finally { inFlight.current = false; setSaving(false); }
  };
  return <Section aria-label="개별 배송지">
    <h3>개별 배송지</h3><p>개별 배송 상품은 상품마다 배송지를 지정할 수 있습니다.</p><p>무료배송 기준이 있는 경우, 개별 배송 상품 금액은 일반 상품의 합계에 포함하지 않습니다.</p>
    {loading && <p role="status">배송지를 불러오는 중입니다.</p>}
    {error && <p role="alert">{error} {!draft && <button type="button" onClick={() => void load()}>다시 시도</button>}</p>}
    {saved && <p role="status">{saved}</p>}
    {!loading && products.map(row => <article key={row.product_seq}>
      <strong>{row.product_name}</strong>
      {draft?.product_seq === row.product_seq ? <form onSubmit={save}>
        {([['addr_recipient', '수령인'], ['addr_phone', '연락처'], ['addr_num', '우편번호'], ['addr_basic', '기본 주소'], ['addr_detail', '상세 주소']] as const).map(([key, label]) =>
          <label key={key}>{label}<input required={key !== 'addr_detail'} maxLength={key === 'addr_recipient' ? 100 : key === 'addr_phone' ? 30 : key === 'addr_num' ? 5 : 255}
            inputMode={key === 'addr_phone' ? 'tel' : key === 'addr_num' ? 'numeric' : 'text'} value={draft[key]} disabled={saving}
            onChange={event => setDraft(current => current ? { ...current, [key]: event.target.value } : null)} /></label>)}
        <div><button type="submit" disabled={saving}>{saving ? '저장 중…' : '배송지 저장'}</button><button type="button" disabled={saving} onClick={() => { setDraft(null); setError(''); }}>닫기</button></div>
      </form> : <><p>{row.addr_recipient} · {row.addr_phone}</p><p>({row.addr_num}) {row.addr_basic} {row.addr_detail}</p>
        {canEdit && <button type="button" disabled={saving} onClick={() => { setDraft({ ...row }); setError(''); setSaved(''); }}>배송지 변경<span className="sr-only"> · {row.product_name}</span></button>}</>}
    </article>)}
    {!loading && !error && !canEdit && products.length > 0 && <p>{reason}</p>}
  </Section>;
}
const Section = styled.section`
  margin-block: 24px; color: var(--user-text); min-width: 0;
  h3 { margin: 0 0 12px; font-size: 17px; }
  p { margin: 8px 0; font-size: 14px; line-height: 1.6; overflow-wrap: anywhere; }
  article { border-top: 1px solid var(--user-border); padding: 16px 0; }
  form { display: grid; gap: 12px; margin-top: 12px; }
  label { display: grid; gap: 6px; font-size: 14px; }
  input { box-sizing: border-box; width: 100%; min-width: 0; min-height: 44px; padding: 10px; border: 1px solid var(--user-border); border-radius: 6px; background: var(--user-surface); color: var(--user-text); font-size: 16px; }
  button { min-height: 44px; padding: 10px 14px; margin-right: 8px; border: 1px solid var(--user-border); border-radius: 6px; background: var(--user-surface); color: var(--user-text); cursor: pointer; }
  input:focus-visible, button:focus-visible { outline: 2px solid var(--user-accent); outline-offset: 2px; }
  button:disabled { opacity: .6; cursor: default; }
  .sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); }
`;

